import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, User } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { itemCount, setIsOpen } = useCart();
  const { user, isAdmin, isB2B } = useAuth();
  const location = useLocation();

  const navLinks = [
    { name: 'Inicio', path: '/' },
    { name: 'Productos', path: '/productos' },
    { name: 'Nosotros', path: '/nosotros' },
    { name: 'Sala de Venta', path: '/sala-venta' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed w-full z-50 glass-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#2C5530] rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">GF</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-cabinet font-bold text-lg text-[#1A231C]">GAMA FOODS</span>
              <span className="block text-xs text-[#808E83] tracking-wider">Chiloé · Sabores Artesanales</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium text-sm transition-colors ${
                  isActive(link.path)
                    ? 'text-[#2C5530]'
                    : 'text-[#4A5D4E] hover:text-[#2C5530]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Link
                to="/admin"
                className="hidden md:flex items-center gap-2 text-sm text-[#4A5D4E] hover:text-[#2C5530] transition-colors"
              >
                <User size={18} />
                Admin
              </Link>
            )}

            {isB2B && (
              <Link
                to="/mi-cuenta"
                className="hidden md:flex items-center gap-2 text-sm text-[#4A5D4E] hover:text-[#2C5530] transition-colors"
              >
                <User size={18} />
                Mi Cuenta
              </Link>
            )}

            {!user && (
              <Link
                to="/login"
                className="hidden md:flex items-center gap-2 text-sm text-[#4A5D4E] hover:text-[#2C5530] transition-colors"
              >
                <User size={18} />
                Acceso
              </Link>
            )}

            {/* Cart Button */}
            <button
              onClick={() => setIsOpen(true)}
              className="relative p-2 rounded-full hover:bg-[#F3F3EF] transition-colors"
            >
              <ShoppingCart size={22} className="text-[#1A231C]" />
              {itemCount > 0 && (
                <span className="cart-badge absolute -top-1 -right-1 w-5 h-5 bg-[#D96C4A] text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </button>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/56994798582?text=Hola!+Quiero+hacer+un+pedido"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-medium text-sm px-4 py-2 rounded-full transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Pedir
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-[#F3F3EF] transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-[#E8EAE6]"
          >
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-[#2C5530] text-white'
                      : 'text-[#4A5D4E] hover:bg-[#F3F3EF]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {isAdmin && (
                <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 rounded-lg font-medium text-[#4A5D4E] hover:bg-[#F3F3EF]">
                  Panel Admin
                </Link>
              )}
              {isB2B && (
                <Link to="/mi-cuenta" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 rounded-lg font-medium text-[#4A5D4E] hover:bg-[#F3F3EF]">
                  Mi Cuenta Empresa
                </Link>
              )}
              {!user && (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-3 px-4 rounded-lg font-medium text-[#4A5D4E] hover:bg-[#F3F3EF]">
                  Iniciar Sesión
                </Link>
              )}
              <a
                href="https://wa.me/56994798582?text=Hola!+Quiero+hacer+un+pedido"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-medium py-3 px-4 rounded-lg mt-4"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Hacer Pedido
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
